import { Form, Input, Button, Card, Row, Col, Select } from "antd";
import { PlusOutlined, DeleteOutlined, MailOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelect } from "@refinedev/antd";

/** Common country phone prefixes */
export const PHONE_PREFIXES = [
  { label: "🇪🇸 +34", value: "+34" },
  { label: "🇮🇹 +39", value: "+39" },
  { label: "🇵🇹 +351", value: "+351" },
  { label: "🇹🇷 +90", value: "+90" },
  { label: "🇪🇬 +20", value: "+20" },
  { label: "🇵🇪 +51", value: "+51" },
  { label: "🇨🇱 +56", value: "+56" },
  { label: "🇺🇾 +598", value: "+598" },
  { label: "🇪🇨 +593", value: "+593" },
  { label: "🇳🇦 +264", value: "+264" },
  { label: "🇿🇼 +263", value: "+263" },
  { label: "🇿🇦 +27", value: "+27" },
  { label: "🇺🇸 +1", value: "+1" },
  { label: "🇬🇧 +44", value: "+44" },
  { label: "🇫🇷 +33", value: "+33" },
];

const PhonePrefixSelector = ({ name }: { name: number }) => (
  <Form.Item name={[name, "phone_prefix"]} noStyle initialValue="+34">
    <Select
      style={{ width: 100 }}
      options={PHONE_PREFIXES}
      showSearch
      filterOption={(input, option) =>
        String(option?.label ?? "").toLowerCase().includes(input.toLowerCase()) ||
        String(option?.value ?? "").includes(input)
      }
    />
  </Form.Item>
);

/**
 * ContactFormList Component
 * Dynamic list of inline contact forms using Ant Design's Form.List.
 */
export const ContactFormList = () => {
  const { t } = useTranslation();

  const { selectProps: positionSelectProps } = useSelect({
    resource: "business-positions",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 100 },
  });

  return (
    <Form.List name="contacts">
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name, ...restField }) => (
            <Card
              key={key}
              size="small"
              style={{ marginBottom: 16 }}
              extra={
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => remove(name)}
                />
              }
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    {...restField}
                    name={[name, "name"]}
                    label={t("contacts.field.name", "Nombre")}
                    rules={[
                      {
                        required: true,
                        message: t("contacts.error.required", "Campo requerido"),
                      },
                    ]}
                  >
                    <Input
                      placeholder={t("contacts.placeholder.name", "Ingrese el nombre")}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    {...restField}
                    name={[name, "surname"]}
                    label={t("contacts.field.surname", "Apellidos")}
                    rules={[
                      {
                        required: true,
                        message: t("contacts.error.required", "Campo requerido"),
                      },
                    ]}
                  >
                    <Input
                      placeholder={t("contacts.placeholder.surname", "Ingrese los apellidos")}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    {...restField}
                    name={[name, "email"]}
                    label={t("contacts.field.email", "Email")}
                    rules={[
                      {
                        type: "email",
                        message: t("contacts.error.invalid_email", "Formato de email inválido"),
                      },
                    ]}
                  >
                    <Input
                      prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
                      placeholder={t("contacts.placeholder.email", "ejemplo@correo.com")}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    {...restField}
                    name={[name, "main_phone"]}
                    label={t("contacts.field.main_phone", "Teléfono Principal")}
                  >
                    <Input
                      addonBefore={<PhonePrefixSelector name={name} />}
                      placeholder={t("contacts.placeholder.main_phone", "600 000 000")}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    {...restField}
                    name={[name, "secondary_phone"]}
                    label={t("contacts.field.secondary_phone", "Teléfono Secundario")}
                  >
                    <Input
                      addonBefore={<PhonePrefixSelector name={name} />}
                      placeholder={t("contacts.placeholder.secondary_phone", "700 000 000")}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    {...restField}
                    name={[name, "identification_number"]}
                    label={t("contacts.field.identification_number", "Número de Identificación")}
                  >
                    <Input
                      placeholder={t("contacts.placeholder.identification_number", "12345678A")}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    {...restField}
                    name={[name, "position_id"]}
                    label={t("contacts.field.position", "Cargo/Posición")}
                  >
                    <Select
                      {...positionSelectProps}
                      placeholder={t("contacts.placeholder.position", "Seleccione un cargo")}
                      allowClear
                      showSearch
                      filterOption={(input, option) =>
                        String(option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          ))}

          <Form.Item>
            <Button
              type="dashed"
              onClick={() => add()}
              block
              icon={<PlusOutlined />}
            >
              {t("producers.actions.add_contact", "Agregar Contacto")}
            </Button>
          </Form.Item>
        </>
      )}
    </Form.List>
  );
};
